import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  Assessment,
  AssessmentQuestion,
  AssociateQualityOrganizationPayload,
  CollectionResponse,
  ComplaintAssessment,
  EmptyResponse,
  ExportReport,
  IntakeQuestionnaireAnswers,
  IntakeQuestionnaireForm,
  IntakeQuestionnaireResults,
  LocationQualityOrganization,
  PaginationQuery,
  PatientAssessment,
  PatientAssessmentAnswer,
  PatientAssessmentDetail,
  PatientIdentifier,
  PromData,
  QualityOrganization,
  QualityOrganizationDcsphAssessment,
  QuestionAnswer,
  Response,
  SubmitAssessmentAnswersPayload,
  TreatmentIdentifier,
} from "~/types/api";

export function createPromApi({
  getRequest,
  postRequest,
  putRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  /** Fetches all PROM assessments. */
  const promAssessments = () => getRequest<CollectionResponse<Assessment>>("/prom/assessments");

  /** Creates a new PROM assessment. */
  const createPromAssessment = (data: {
    code: string;
    name: string;
    klinimetrie_code?: string | null;
    description?: string;
  }) => postRequest<Response<Assessment>>("/prom/assessments", data);

  /** Updates an existing PROM assessment. */
  const updatePromAssessment = (
    id: number,
    data: {
      code: string;
      name: string;
      klinimetrie_code?: string | null;
      description?: string;
    },
  ) => putRequest<Response<Assessment>>(`/prom/assessments/${id}`, data);

  /** Deletes a PROM assessment. */
  const deletePromAssessment = (id: number) => deleteRequest<EmptyResponse>(`/prom/assessments/${id}`);

  /** Creates a new question for a PROM assessment. */
  const createPromQuestion = (
    assessmentId: number,
    data: {
      code: string;
      name: string;
      type: string;
      options: Array<{ label: string; key: string; score: number }>;
      order: number;
    },
  ) => postRequest<Response<AssessmentQuestion>>(`/prom/assessments/${assessmentId}/questions`, data);

  /** Updates an existing question in a PROM assessment. */
  const updatePromQuestion = (
    assessmentId: number,
    questionId: number,
    data: {
      code: string;
      name: string;
      type: string;
      options: Array<{ label: string; key: string; score: number }>;
      order: number;
    },
  ) => putRequest<Response<AssessmentQuestion>>(`/prom/assessments/${assessmentId}/questions/${questionId}`, data);

  /** Deletes a question from a PROM assessment. */
  const deletePromQuestion = (assessmentId: number, questionId: number) =>
    deleteRequest<EmptyResponse>(`/prom/assessments/${assessmentId}/questions/${questionId}`);

  /** Fetches all quality organizations. */
  const promOrganizations = () => getRequest<CollectionResponse<QualityOrganization>>("/prom/organizations");

  /** Creates a new quality organization. */
  const createPromOrganization = (data: { name: string }) =>
    postRequest<Response<QualityOrganization>>("/prom/organizations", data);

  /** Updates an existing quality organization. */
  const updatePromOrganization = (id: number, data: { name: string }) =>
    putRequest<Response<QualityOrganization>>(`/prom/organizations/${id}`, data);

  /** Deletes a quality organization. */
  const deletePromOrganization = (id: number) => deleteRequest<EmptyResponse>(`/prom/organizations/${id}`);

  /** Fetches all DCSPH-assessment couplings for quality organizations. */
  const promCouplings = () =>
    getRequest<CollectionResponse<QualityOrganizationDcsphAssessment>>("/prom/organization-dcsph-assessments");

  /** Fetches a paginated list of DCSPH-assessment couplings, optionally filtered by DCSPH code. */
  const listPromCouplings = (pagination: PaginationQuery, dcsphCodeFilter?: string) => {
    const filterBy = dcsphCodeFilter
      ? [
          {
            column: "dcsph_code",
            value: dcsphCodeFilter,
          },
        ]
      : undefined;

    return getPaginationRequest<QualityOrganizationDcsphAssessment>(
      "/prom/organization-dcsph-assessments/list",
      pagination,
      undefined,
      undefined,
      filterBy,
    );
  };

  /** Creates a coupling between a quality organization, DCSPH code, and assessment. */
  const createPromCoupling = (data: {
    quality_organization_id: number;
    dcsph_code: string;
    assessment_id: number;
    period: "start" | "6_weeks" | "every_3_months" | "end";
  }) => postRequest<Response<QualityOrganizationDcsphAssessment>>("/prom/organization-dcsph-assessments", data);

  /** Creates multiple DCSPH-assessment couplings in bulk. */
  const createMultiplePromCouplings = (data: {
    quality_organization_id: number;
    assessment_id: number;
    dcsph_codes: string[];
    periods: ("start" | "6_weeks" | "every_3_months" | "end")[];
  }) =>
    postRequest<Response<{ created_count: number; message: string }>>(
      "/prom/organization-dcsph-assessments/create-multiple",
      data,
    );

  /** Updates an existing DCSPH-assessment coupling. */
  const updatePromCoupling = (
    id: number,
    data: {
      quality_organization_id: number;
      dcsph_code: string;
      assessment_id: number;
      period: "start" | "6_weeks" | "every_3_months" | "end";
    },
  ) => putRequest<Response<QualityOrganizationDcsphAssessment>>(`/prom/organization-dcsph-assessments/${id}`, data);

  /** Deletes a DCSPH-assessment coupling. */
  const deletePromCoupling = (id: number) => deleteRequest<EmptyResponse>(`/prom/organization-dcsph-assessments/${id}`);

  /** Fetches the national couplings of intake complaints to assessments. */
  const promComplaintAssessments = () =>
    getRequest<CollectionResponse<ComplaintAssessment>>("/prom/complaint-assessments");

  /** Replaces the whole set of assessments coupled to one complaint. */
  const syncPromComplaintAssessments = (complaint: string, assessmentIds: number[]) =>
    putRequest<CollectionResponse<ComplaintAssessment>>(`/prom/complaint-assessments/${complaint}`, {
      assessment_ids: assessmentIds,
    });

  /** Fetches answers for a patient assessment. */
  const getPatientAssessmentAnswers = (patientAssessmentId: string) =>
    getRequest<{ data: PatientAssessmentAnswer[] }>(`/prom/patient-assessments/${patientAssessmentId}/answers`);

  /** Stores new answers for a patient assessment. */
  const storePatientAssessmentAnswers = (
    patientAssessmentId: string,
    data: { answers: Array<{ assessment_question_id: number; answer: QuestionAnswer }> },
  ) =>
    postRequest<{ data: PatientAssessmentAnswer[] }>(`/prom/patient-assessments/${patientAssessmentId}/answers`, data);

  /** Updates existing answers for a patient assessment. */
  const updatePatientAssessmentAnswers = (
    patientAssessmentId: string,
    data: { answers: Array<{ assessment_question_id: number; answer: QuestionAnswer }> },
  ) =>
    putRequest<{ data: PatientAssessmentAnswer[] }>(`/prom/patient-assessments/${patientAssessmentId}/answers`, data);

  /** Fetches all patient assessments for a treatment. */
  const getPatientAssessments = (treatmentId: number) =>
    getRequest<CollectionResponse<PatientAssessment>>(`/prom/treatments/${treatmentId}/patient-assessments`);

  /** Fetches patient assessments that have been sent for a treatment. */
  const getSentPatientAssessments = (treatmentId: number) =>
    getRequest<CollectionResponse<PatientAssessment>>(`/prom/treatments/${treatmentId}/patient-assessments/sent`);

  /** Fetches details of a single patient assessment. */
  /** The questionnaires the patient of an intake got beforehand; `data` is null when none went out. */
  const intakeQuestionnaireResults = (appointmentId: number) =>
    getRequest<Response<IntakeQuestionnaireResults | null>>(`/prom/appointments/${appointmentId}/intake-questionnaires`);

  const intakeQuestionnaireAnswers = (appointmentId: number, questionnaireId: string) =>
    getRequest<Response<IntakeQuestionnaireAnswers>>(
      `/prom/appointments/${appointmentId}/intake-questionnaires/${questionnaireId}`,
    );

  /** Adds the questionnaires of the complaint region that did not go out, so the practitioner can fill them in. */
  const addMissingIntakeQuestionnaires = (appointmentId: number) =>
    postRequest<Response<IntakeQuestionnaireResults | null>>(
      `/prom/appointments/${appointmentId}/intake-questionnaires`,
      {},
    );

  const intakeQuestionnaireForm = (appointmentId: number, questionnaireId: string) =>
    getRequest<Response<IntakeQuestionnaireForm>>(
      `/prom/appointments/${appointmentId}/intake-questionnaires/${questionnaireId}/answers`,
    );

  /** Stores answers the practitioner enters with the patient; the backend records who entered them. */
  const saveIntakeQuestionnaireAnswersAsPractitioner = (
    appointmentId: number,
    questionnaireId: string,
    payload: SubmitAssessmentAnswersPayload,
  ) =>
    putRequest<Response<IntakeQuestionnaireForm>>(
      `/prom/appointments/${appointmentId}/intake-questionnaires/${questionnaireId}/answers`,
      payload,
    );

  const getPatientAssessment = (patientAssessmentId: string) =>
    getRequest<Response<PatientAssessmentDetail>>(`/prom/patient-assessments/${patientAssessmentId}`);

  /** Fetches quality organizations associated with a location. */
  const getLocationQualityOrganizations = (locationId: number) =>
    getRequest<CollectionResponse<LocationQualityOrganization>>(`/prom/locations/${locationId}/quality-organizations`);

  /** Associates a quality organization with a location. */
  const associateQualityOrganizationWithLocation = (locationId: number, data: AssociateQualityOrganizationPayload) =>
    postRequest<Response<{ message: string }>>(`/prom/locations/${locationId}/quality-organizations`, data);

  /** Removes a quality organization from a location. */
  const removeQualityOrganizationFromLocation = (locationId: number, qualityOrganizationId: number) =>
    deleteRequest<Response<{ message: string }>>(
      `/prom/locations/${locationId}/quality-organizations/${qualityOrganizationId}`,
    );

  /** Generates an export report for a quality organization at a location. */
  const generateQualityOrganizationExport = (
    locationId: number,
    qualityOrganizationId: number,
    body?: { start_date: string; end_date: string },
  ) =>
    postRequest<Response<ExportReport>>(
      `/prom/locations/${locationId}/quality-organizations/${qualityOrganizationId}/export`,
      body ?? {},
    );

  /** Fetches PROM data for a patient's treatment. */
  const patientProm = (patientId: PatientIdentifier, treatmentId: TreatmentIdentifier) =>
    getRequest<Response<Record<string, PromData>>>(`/patients/${patientId}/treatment/${treatmentId}/prom`);

  return {
    promAssessments,
    createPromAssessment,
    updatePromAssessment,
    deletePromAssessment,
    createPromQuestion,
    updatePromQuestion,
    deletePromQuestion,
    promOrganizations,
    createPromOrganization,
    updatePromOrganization,
    deletePromOrganization,
    promCouplings,
    listPromCouplings,
    createPromCoupling,
    createMultiplePromCouplings,
    updatePromCoupling,
    deletePromCoupling,
    promComplaintAssessments,
    syncPromComplaintAssessments,
    getPatientAssessmentAnswers,
    storePatientAssessmentAnswers,
    updatePatientAssessmentAnswers,
    getPatientAssessments,
    getSentPatientAssessments,
    getPatientAssessment,
    intakeQuestionnaireResults,
    intakeQuestionnaireAnswers,
    addMissingIntakeQuestionnaires,
    intakeQuestionnaireForm,
    saveIntakeQuestionnaireAnswersAsPractitioner,
    getLocationQualityOrganizations,
    associateQualityOrganizationWithLocation,
    removeQualityOrganizationFromLocation,
    generateQualityOrganizationExport,
    patientProm,
  };
}

export type PromApi = ReturnType<typeof createPromApi>;
